import { useEffect, useMemo, useState, useRef } from "react";
import { useLeague } from "./LeagueLayout";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Trophy, Upload, Save, RefreshCw, Info, Sparkles, Link2, Loader2, Settings } from "lucide-react";

export default function LeagueClassifica() {
  const { league, isAdmin } = useLeague();
  const [searchParams] = useSearchParams();
  const adminMode = isAdmin && searchParams.get("admin") === "1";
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Import state
  const [giornata, setGiornata] = useState(1);
  const [preview, setPreview] = useState(null);   // { matched, unmatched, league_teams, total_rows }
  const [mapping, setMapping] = useState({});     // {raw_name_lower: team_id}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [syncUrl, setSyncUrl] = useState("");
  const [syncGiornata, setSyncGiornata] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const fileRef = useRef(null);

  const loadCurrent = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/standings`);
      setCurrent(r.data);
      if (r.data?.giornata) setGiornata(Math.min(38, (r.data.giornata || 0) + 1));
    } catch (e) {
      // 404 or empty is fine
      setCurrent(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadCurrent(); }, [league.league_id]);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setPreview(null); setMapping({});
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("giornata", String(giornata));
      const r = await api.post(`/leagues/${league.league_id}/standings/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      toast.success(`File letto: ${r.data.matched.length} match, ${r.data.unmatched.length} da mappare`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onMappingChange = (rawName, teamId) => {
    setMapping(m => ({ ...m, [rawName.toLowerCase()]: teamId }));
  };

  const unresolvedCount = useMemo(() => {
    if (!preview) return 0;
    return preview.unmatched.filter(u => !mapping[u.raw_name.toLowerCase()]).length;
  }, [preview, mapping]);

  const save = async () => {
    if (!preview) return;
    // Compose all rows including newly-mapped ones
    const rows = [
      ...preview.matched.map(m => ({ ...m })),
      ...preview.unmatched.map(u => {
        const tid = mapping[u.raw_name.toLowerCase()];
        return { ...u, team_id: tid || null };
      }),
    ];
    setSaving(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/standings/save`, {
        giornata: preview.giornata,
        rows,
        mapping,
        require_all_matched: true,
      });
      toast.success(`✅ Classifica salvata: giornata ${r.data.giornata}, ${r.data.matched_rows} squadre`);
      setPreview(null); setMapping({});
      loadCurrent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const abort = () => { setPreview(null); setMapping({}); };

  const syncFromUrl = async () => {
    if (!syncUrl || !syncUrl.startsWith("http")) {
      toast.error("Inserisci un URL valido (deve iniziare con http/https)");
      return;
    }
    setSyncing(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/standings/fetch-url`, {
        url: syncUrl, giornata: syncGiornata,
      });
      setPreview(r.data); setMapping({});
      toast.success(`✅ Classifica letta: ${r.data.matched.length} match, ${r.data.unmatched.length} da mappare`);
      setUrlModalOpen(false);
      setSyncUrl("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6" data-testid="league-classifica-page">
      {/* Hero */}
      <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-slate-900 via-slate-950 to-amber-950/20 p-5 sm:p-7 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none"/>
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <Trophy className="text-amber-400" size={22}/>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white">Classifica</h1>
          </div>
          <div className="flex-1 text-sm text-slate-300">
            {current
              ? <>Ultima importazione: <span className="font-black text-white">giornata {current.giornata}</span> · <span className="text-slate-500">{new Date(current.updated_at).toLocaleString("it-IT")}</span></>
              : <>Nessuna classifica ancora importata. L&apos;admin può caricare un Excel Fantacalcio.it (colonne: Posizione, Squadra, Punti, V, N, P, GF, GS).</>}
          </div>
        </div>
      </div>

      {/* Current standings display */}
      {current && current.rows?.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:p-5" data-testid="current-standings">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-black uppercase tracking-wider text-white flex items-center gap-2">
              <Sparkles size={16} className="text-amber-400"/> Classifica giornata {current.giornata}
            </h2>
            <button onClick={loadCurrent} className="text-slate-400 hover:text-white" data-testid="reload-standings">
              <RefreshCw size={14}/>
            </button>
          </div>
          <StandingsTable rows={current.rows}/>
        </div>
      )}

      {isAdmin && !adminMode && (
        <div className="flex justify-end">
          <Link to={`/lega/${league.league_id}/decisioni`}
            data-testid="link-classifica-admin"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-amber-300">
            <Settings size={12}/> Gestisci come admin →
          </Link>
        </div>
      )}

      {/* Admin — import & mapping */}
      {adminMode && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:p-5" data-testid="admin-import-panel">
          <h2 className="text-lg font-black uppercase tracking-wider text-white mb-3 flex items-center gap-2">
            <Upload size={16} className="text-emerald-400"/> Importa classifica (admin)
          </h2>

          {!preview && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-black block mb-1">Giornata</label>
                  <input type="number" min={0} max={99} value={giornata}
                    onChange={e => setGiornata(parseInt(e.target.value) || 1)}
                    data-testid="input-giornata"
                    className="w-20 px-2 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white font-mono"/>
                </div>
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-black block mb-1">File Excel/CSV Fantacalcio.it</label>
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFileChange}
                    disabled={uploading}
                    data-testid="input-file"
                    className="text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-slate-900 file:font-black file:cursor-pointer"/>
                  {uploading && <div className="mt-1 text-[10px] text-slate-500">Caricamento…</div>}
                </div>
                <button onClick={() => { setSyncGiornata(giornata); setUrlModalOpen(true); }}
                  data-testid="open-url-sync"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest bg-slate-800 border border-slate-700 hover:border-emerald-500/60 text-emerald-300">
                  <Link2 size={13}/> Sincronizza da URL
                </button>
              </div>
              <p className="text-[10px] text-slate-500">
                Puoi caricare un file esportato oppure incollare l&apos;URL della pagina classifica (Fantacalcio.it lega pubblica o altra piattaforma con classifica in HTML).
              </p>
            </div>
          )}

          {preview && (
            <div className="space-y-4" data-testid="preview-panel">
              <div className="flex items-center gap-3 text-xs flex-wrap">
                <span className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold">✓ {preview.matched.length} match</span>
                <span className={`px-2 py-1 rounded-full font-bold border ${unresolvedCount === 0 ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-rose-500/20 text-rose-300 border-rose-500/40"}`}>
                  {unresolvedCount === 0 ? "✓ Tutte mappate" : `${unresolvedCount} da mappare`}
                </span>
                <span className="text-slate-500 font-mono">Giornata {preview.giornata}</span>
                {preview.source_url && (
                  <span className="text-slate-500 font-mono flex items-center gap-1 max-w-full truncate" data-testid="source-url-badge">
                    <Link2 size={11}/><span className="truncate">{preview.source_url}</span>
                  </span>
                )}
              </div>

              {preview.unmatched.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1 flex items-center gap-1">
                    <Info size={11}/> Squadre non riconosciute — associa manualmente
                  </div>
                  <div className="space-y-2">
                    {preview.unmatched.map(u => (
                      <div key={u.raw_name}
                        data-testid={`unmatched-row-${u.raw_name}`}
                        className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center bg-slate-950/50 rounded-xl p-2 border border-slate-800">
                        <div className="text-sm">
                          <div className="font-bold text-white">{u.raw_name}</div>
                          <div className="text-[10px] font-mono text-slate-500">pos {u.position} · {u.points} pts</div>
                        </div>
                        <div className="sm:col-span-2">
                          <select value={mapping[u.raw_name.toLowerCase()] || ""}
                            onChange={e => onMappingChange(u.raw_name, e.target.value)}
                            data-testid={`map-select-${u.raw_name}`}
                            className="w-full px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white">
                            <option value="">— Associa a un team —</option>
                            {preview.league_teams.map(t => (
                              <option key={t.team_id} value={t.team_id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full preview table */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">Anteprima classifica</div>
                <PreviewTable preview={preview} mapping={mapping}/>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-800">
                <button onClick={abort} disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300 border border-slate-700 hover:border-slate-500"
                  data-testid="abort-import">Annulla</button>
                <button onClick={save} disabled={saving || unresolvedCount > 0}
                  data-testid="save-standings"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest bg-emerald-500 text-slate-900 hover:bg-emerald-400 disabled:opacity-40">
                  <Save size={13}/> {saving ? "Salvo…" : `Salva classifica giornata ${preview.giornata}`}
                </button>
                {unresolvedCount > 0 && (
                  <div className="text-[11px] text-rose-300">Mappa tutte le squadre non riconosciute per poter salvare.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* URL sync modal */}
      {urlModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
          data-testid="url-sync-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !syncing) setUrlModalOpen(false); }}>
          <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="text-emerald-400" size={18}/>
              <div className="font-black uppercase tracking-widest text-white text-sm">Sincronizza classifica da URL</div>
            </div>
            <p className="text-xs text-slate-400 mb-3">
              Incolla l&apos;URL pubblico della pagina classifica della tua lega (es. Fantacalcio.it lega pubblica).
              Il sistema scaricherà la pagina, cercherà la tabella con Squadra + Punti e ti mostrerà l&apos;anteprima.
              Per leghe private, esporta l&apos;Excel manualmente.
            </p>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-black block mb-1">URL classifica</label>
            <input type="url" value={syncUrl} onChange={e => setSyncUrl(e.target.value)}
              placeholder="https://leghe.fantacalcio.it/mia-lega/classifica"
              data-testid="input-sync-url"
              disabled={syncing}
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm mb-3 disabled:opacity-40"/>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-black block mb-1">Giornata</label>
            <input type="number" min={0} max={99} value={syncGiornata}
              onChange={e => setSyncGiornata(parseInt(e.target.value) || 1)}
              disabled={syncing}
              data-testid="input-sync-giornata"
              className="w-20 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm font-mono mb-4 disabled:opacity-40"/>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setUrlModalOpen(false)} disabled={syncing}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-30"
                data-testid="cancel-url-sync">Annulla</button>
              <button onClick={syncFromUrl} disabled={syncing || !syncUrl}
                data-testid="submit-url-sync"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest bg-emerald-500 text-slate-900 hover:bg-emerald-400 disabled:opacity-30">
                {syncing ? <><Loader2 className="animate-spin" size={13}/> Scarico…</> : <><Link2 size={13}/> Sincronizza</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StandingsTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <th className="text-left px-2 py-2">#</th>
            <th className="text-left px-2 py-2">Squadra</th>
            <th className="text-right px-2 py-2">Pt</th>
            <th className="text-right px-2 py-2 hidden sm:table-cell">V</th>
            <th className="text-right px-2 py-2 hidden sm:table-cell">N</th>
            <th className="text-right px-2 py-2 hidden sm:table-cell">P</th>
            <th className="text-right px-2 py-2 hidden md:table-cell">GF</th>
            <th className="text-right px-2 py-2 hidden md:table-cell">GS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.team_id || r.raw_name || i}
              data-testid={`row-team-${r.team_id}`}
              className={`border-t border-slate-800 hover:bg-slate-800/40 ${
                r.position === 1 ? "bg-amber-500/5" :
                r.position <= 3 ? "bg-emerald-500/5" :
                r.position >= rows.length - 1 ? "bg-rose-500/5" : ""
              }`}>
              <td className="px-2 py-1.5 font-mono text-slate-400">{r.position}</td>
              <td className="px-2 py-1.5 font-bold text-white">{r.team_name}</td>
              <td className="px-2 py-1.5 text-right font-black text-amber-300">{r.points}</td>
              <td className="px-2 py-1.5 text-right text-emerald-300 hidden sm:table-cell">{r.w}</td>
              <td className="px-2 py-1.5 text-right text-slate-300 hidden sm:table-cell">{r.d}</td>
              <td className="px-2 py-1.5 text-right text-rose-300 hidden sm:table-cell">{r.l}</td>
              <td className="px-2 py-1.5 text-right font-mono hidden md:table-cell">{r.gf}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-500 hidden md:table-cell">{r.ga}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreviewTable({ preview, mapping }) {
  const all = [
    ...preview.matched.map(m => ({ ...m, _match: true })),
    ...preview.unmatched.map(u => {
      const tid = mapping[u.raw_name.toLowerCase()];
      const team = tid ? preview.league_teams.find(t => t.team_id === tid) : null;
      return { ...u, team_id: tid, team_name: team?.name, _match: !!team };
    }),
  ];
  all.sort((a, b) => a.position - b.position);
  return (
    <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-800">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-slate-900">
          <tr className="text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <th className="text-left px-2 py-1.5">#</th>
            <th className="text-left px-2 py-1.5">Excel</th>
            <th className="text-left px-2 py-1.5">→ Team</th>
            <th className="text-right px-2 py-1.5">Pt</th>
          </tr>
        </thead>
        <tbody>
          {all.map((r, i) => (
            <tr key={i} className="border-t border-slate-800">
              <td className="px-2 py-1 font-mono text-slate-400">{r.position}</td>
              <td className="px-2 py-1 text-slate-300">{r.raw_name}</td>
              <td className="px-2 py-1 font-bold">
                {r._match ? <span className="text-emerald-300">{r.team_name}</span> : <span className="text-rose-300">— mancante —</span>}
              </td>
              <td className="px-2 py-1 text-right font-mono text-amber-300">{r.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
